import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import MerkezSecici from '@/components/MerkezSecici';
import { useProducts } from '@/contexts/ProductsContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import ProductCombobox from '@/components/ProductCombobox';
import SearchableSelect from '@/components/SearchableSelect';
import BelgeGorseli from '@/components/BelgeGorseli';
import { ArrowLeft, Save, Plus, Trash2, PackageCheck, Link2, Sparkles, Upload, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { birimSecenekleri } from '@/lib/birimler';
import { trNorm } from '@/lib/arama';

// Mal kabul = teslimat belgesi. Fatura ekranıyla AYNI kalem mantığı: ürün
// eşleştirme + ambalaj üçlüsü (adet / birim / içerik). Fiyat burada OPSİYONEL
// ama sistem onu son alış fiyatından DOLDURMAZ — uydurulan fiyat maliyeti
// sessizce bozar (bkz. backend/routes/teslimat.py başlığı).
const BELGE_TURLERI = [
  { deger: 'irsaliye', etiket: 'İrsaliye' },
  { deger: 'veresiye_fisi', etiket: 'Veresiye Fişi' },
  { deger: 'siparis_fisi', etiket: 'Sipariş Fişi' },
];

const bugun = () => new Date().toLocaleDateString('en-CA');
const bosKalem = (depo) => ({
  _yerel: Math.random().toString(36).slice(2, 9),
  _type: 'product', description: '', matched_product_id: '',
  matched_expense_id: '', matched_fixed_asset_type_id: '',
  quantity: '', unit: '', package_qty: '', unit_price: '',
  warehouse_id: depo || '', siparis_ref: null,
});

// KALEM TÜRÜ (kullanıcı, 2026-10-03: "veresiye fişinde mazot da aldık… gider
// kalemi de olabilir demirbaş da"). Form bir dönem yalnızca ürün seçtiriyordu
// ve her satırda depo soruyordu; sunucu ise türü zaten taşıyordu
// (`teslimat._kalemleri_hazirla`). Gider ve demirbaş STOĞA YAZILMAZ, deposu ve
// ambalaj içeriği sorulmaz; faturaya bağlanınca türü faturaya geçer.
const KALEM_TURLERI = [
  { deger: 'product', etiket: 'Stoklu' },
  { deger: 'expense', etiket: 'Gider' },
  { deger: 'fixed_asset', etiket: 'Demirbaş' },
];

export default function MalKabulForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/mal-kabul');
  const duzenleme = Boolean(id);
  usePageTitle(duzenleme ? 'Mal Kabul — Düzenle' : 'Yeni Mal Kabul');

  const { products } = useProducts();
  const [units, setUnits] = useState([]);
  const [giderKalemleri, setGiderKalemleri] = useState([]);
  const [demirbasTurleri, setDemirbasTurleri] = useState([]);
  // Belge görseli: kutudan gelindiyse kutu sayfaları, düzenlemede mal kabule
  // bağlı belgenin sayfaları (mal kabul yetkisiyle okunur).
  const [gorsel, setGorsel] = useState(null);   // { sayfalar, adres }
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const [form, setForm] = useState({
    belge_turu: 'irsaliye', belge_no: '', supplier_id: '',
    teslim_tarihi: bugun(), warehouse_id: '', notlar: '',
    // Kârlılık merkez bazında; teslimat da bir işlem kaydı (bkz. MerkezSecici).
    cost_center_id: '',
    purchase_order_id: params.get('siparis') || '',
  });
  const [kalemler, setKalemler] = useState([]);
  const [siparisKalemleri, setSiparisKalemleri] = useState([]);
  const [siparisAcik, setSiparisAcik] = useState(false);
  const [okunuyor, setOkunuyor] = useState(false);
  const [kutuBelgeId, setKutuBelgeId] = useState(params.get('kutu_belge') || '');
  const [okunanCari, setOkunanCari] = useState('');

  useEffect(() => {
    api.get('/admin/units').then(r => setUnits(r.data || [])).catch(() => {});
    api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data || [])).catch(() => {});
    api.get('/expense-items').then(r => setGiderKalemleri(r.data || [])).catch(() => {});
    api.get('/fixed-asset-types').then(r => setDemirbasTurleri(r.data || [])).catch(() => {});
    const kutu = params.get('kutu_belge');
    if (kutu) {
      api.get(`/dosya-kutusu/${kutu}`).then(r => setGorsel({
        sayfalar: (r.data?.sayfalar || []).map(x => ({ sira: x.sira, content_type: x.content_type })),
        adres: (sira) => `/dosya-kutusu/${kutu}/sayfa/${sira}`,
      })).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!duzenleme) { setKalemler([bosKalem('')]); return; }
    setYukleniyor(true);
    api.get(`/teslimat/${id}`).then(r => {
      const b = r.data;
      setForm({
        belge_turu: b.belge_turu || 'irsaliye', belge_no: b.belge_no || '',
        supplier_id: b.supplier_id || '', teslim_tarihi: b.teslim_tarihi || bugun(),
        cost_center_id: b.cost_center_id || '',
        warehouse_id: b.warehouse_id || '', notlar: b.notlar || '',
        purchase_order_id: b.purchase_order_id || '',
      });
      if ((b.kutu_sayfalari || []).length) {
        setGorsel({ sayfalar: b.kutu_sayfalari, adres: (sira) => `/teslimat/${id}/kutu-sayfa/${sira}` });
      }
      setKalemler((b.kalemler || []).map(k => ({
        ...k, _yerel: k.kalem_id,
        matched_expense_id: k.matched_expense_id || '',
        matched_fixed_asset_type_id: k.matched_fixed_asset_type_id || '',
        quantity: k.quantity != null ? String(k.quantity) : '',
        package_qty: k.package_qty != null ? String(k.package_qty) : '',
        unit_price: k.unit_price != null ? String(k.unit_price) : '',
      })));
    }).catch(() => toast.error('Belge yüklenemedi'))
      .finally(() => setYukleniyor(false));
  }, [id, duzenleme]);

  // Cari seçilince o cariden AÇIK sipariş kalemleri getirilir — siparişten
  // mal kabul bu listeden yapılır.
  const siparisleriGetir = useCallback(async (supplierId) => {
    if (!supplierId) { setSiparisKalemleri([]); return; }
    try {
      const r = await api.get('/teslimat/siparis-kalemleri', {
        params: { supplier_id: supplierId } });
      setSiparisKalemleri(r.data?.kalemler || []);
    } catch { setSiparisKalemleri([]); }
  }, []);

  useEffect(() => { siparisleriGetir(form.supplier_id); }, [form.supplier_id, siparisleriGetir]);

  // AI çıktısını forma yerleştirir. Kayıt YAPMAZ — kullanıcı kontrol edip
  // kaydeder. Fiyat geldiği gibi konur; boşsa boş kalır (sistem son alış
  // fiyatından doldurmuyor, bkz. backend/routes/teslimat.py).
  const okunaniYerlestir = useCallback((d) => {
    setForm(f => ({
      ...f,
      belge_no: d.belge_no || f.belge_no,
      teslim_tarihi: d.teslim_tarihi || f.teslim_tarihi,
      supplier_id: d.supplier_id || f.supplier_id,
      belge_turu: d.belge_turu || f.belge_turu,
    }));
    setOkunanCari(d.parsed_supplier_name || '');
    const gelen = (d.kalemler || []).map(k => ({
      ...bosKalem(''),
      _type: k._type || 'product',
      description: k.description || '',
      matched_product_id: k.matched_product_id || '',
      matched_product_name: k.matched_product_name || '',
      matched_expense_id: k.matched_expense_id || '',
      matched_fixed_asset_type_id: k.matched_fixed_asset_type_id || '',
      quantity: k.quantity != null ? String(k.quantity) : '',
      unit: k.unit || '',
      package_qty: k.package_qty != null ? String(k.package_qty) : '',
      unit_price: k.unit_price != null ? String(k.unit_price) : '',
    }));
    if (gelen.length) setKalemler(gelen);
    toast.success(`${gelen.length} kalem okundu — kontrol edip kaydedin`);
  }, []);

  // Dosya kutusundan gelindiyse sayfaları sunucu okur (tarayıcı indirmez).
  useEffect(() => {
    const kutu = params.get('kutu_belge');
    if (!kutu || duzenleme) return;
    setOkunuyor(true);
    api.post(`/teslimat/parse-belge/${kutu}`)
      .then(r => okunaniYerlestir(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Belge okunamadı'))
      .finally(() => setOkunuyor(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duzenleme]);

  const dosyadanOku = async (dosya) => {
    if (!dosya) return;
    setOkunuyor(true);
    const fd = new FormData();
    fd.append('file', dosya);
    try {
      const r = await api.post('/teslimat/parse', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      okunaniYerlestir(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Belge okunamadı', { duration: 7000 });
    }
    setOkunuyor(false);
  };

  const kart = useCallback(
    (k) => products.find(p => p.id === k.matched_product_id) || null, [products]);

  const kalemGuncelle = (i, alan, deger) => {
    setKalemler(prev => {
      const yeni = [...prev];
      yeni[i] = { ...yeni[i], [alan]: deger };
      // Tür değişince ÖNCEKİ türün eşleşmesi temizlenir (faturadaki kural):
      // ürün olarak eşleşmiş satır gidere çevrilince ürün kaydı kalırsa sistem
      // onu hâlâ stoklu sayardı. Gider/demirbaşın deposu ve içeriği yoktur.
      if (alan === '_type') {
        if (deger !== 'product') {
          yeni[i] = { ...yeni[i], matched_product_id: '', matched_product_name: '',
                      warehouse_id: '', package_qty: '' };
        } else if (!yeni[i].warehouse_id) {
          yeni[i].warehouse_id = form.warehouse_id || '';
        }
        if (deger !== 'expense') yeni[i].matched_expense_id = '';
        if (deger !== 'fixed_asset') yeni[i].matched_fixed_asset_type_id = '';
      }
      if (alan === 'matched_expense_id') {
        const g = giderKalemleri.find(x => x.id === deger);
        if (g && !yeni[i].description) yeni[i].description = g.name;
      }
      if (alan === 'matched_fixed_asset_type_id') {
        const d = demirbasTurleri.find(x => x.id === deger);
        if (d && !yeni[i].description) yeni[i].description = d.name;
      }
      // Ürün seçilince birim ve içerik karttan gelir — ama FİYAT gelmez.
      if (alan === 'matched_product_id') {
        const p = products.find(x => x.id === deger);
        if (p) {
          yeni[i].matched_product_name = p.name;
          if (!yeni[i].unit) yeni[i].unit = p.unit || '';
          if (!yeni[i].description) yeni[i].description = p.name;
        }
      }
      // Birim ürünün ana birimiyse içerik 1'dir; farklı birimde kullanıcı girer.
      if (alan === 'unit' || alan === 'matched_product_id') {
        const p = products.find(x => x.id === yeni[i].matched_product_id);
        const secilen = alan === 'unit' ? deger : yeni[i].unit;
        if (p && secilen) {
          const es = birimSecenekleri(p, units)
            .find(o => trNorm(o.deger) === trNorm(secilen));
          if (es && es.carpan > 0) yeni[i].package_qty = String(es.carpan);
        }
      }
      return yeni;
    });
  };

  const siparistenEkle = (sk) => {
    const p = products.find(x => x.id === sk.product_card_id);
    setKalemler(prev => {
      const temiz = prev.filter(k => k.matched_product_id || k.description);
      return [...temiz, {
        ...bosKalem(form.warehouse_id),
        _type: 'product',
        matched_product_id: sk.product_card_id,
        matched_product_name: sk.product_name,
        description: sk.product_name,
        quantity: String(sk.kalan_miktar),
        unit: sk.unit || p?.unit || '',
        unit_price: sk.unit_price != null ? String(sk.unit_price) : '',
        siparis_ref: { order_id: sk.order_id, kalem_idx: sk.kalem_idx,
                       miktar: sk.kalan_miktar },
      }];
    });
    if (!form.purchase_order_id) {
      setForm(f => ({ ...f, purchase_order_id: sk.order_id }));
    }
    setSiparisAcik(false);
    toast.success(`${sk.product_name} eklendi — miktarı düzenleyebilirsiniz`);
  };

  const toplam = useMemo(() => kalemler.reduce(
    (t, k) => t + (parseFloat(k.quantity) || 0) * (parseFloat(k.unit_price) || 0), 0),
    [kalemler]);

  const kaydet = async () => {
    if (!form.supplier_id) {
      toast.error('Cari hesap (tedarikçi) seçilmeli'); return;
    }
    const dolu = kalemler.filter(k => k.matched_product_id || k.matched_expense_id
      || k.matched_fixed_asset_type_id || k.description);
    const eslesmemis = dolu.filter(k => (k._type === 'expense' && !k.matched_expense_id)
      || (k._type === 'fixed_asset' && !k.matched_fixed_asset_type_id));
    if (eslesmemis.length) {
      toast.error(`${eslesmemis.length} gider/demirbaş kaleminde eşleşme seçilmedi`); return;
    }
    if (!dolu.length) { toast.error('En az bir kalem girilmeli'); return; }
    // Ambalaj birimi faturada olduğu gibi burada da ZORUNLU — satın alma
    // verisi eksik kalmamalı. Sunucu da reddediyor; bu kontrol kullanıcıyı
    // doğrudan satıra götürmek için.
    const birimsiz = dolu.filter(k => k._type === 'product' && !(k.unit || '').trim());
    if (birimsiz.length) {
      toast.error(`${birimsiz.length} kalemde ambalaj birimi boş. Belgedeki birimi seçin.`);
      return;
    }
    setKaydediliyor(true);
    const govde = {
      ...form,
      purchase_order_id: form.purchase_order_id || null,
      kutu_belge_id: kutuBelgeId || null,
      kalemler: dolu.map(k => ({
        kalem_id: k.kalem_id || undefined,
        _type: k._type || 'product',
        description: k.description,
        matched_product_id: k._type === 'product' ? (k.matched_product_id || null) : null,
        matched_product_name: k._type === 'product' ? (k.matched_product_name || null) : null,
        matched_expense_id: k._type === 'expense' ? (k.matched_expense_id || null) : null,
        matched_fixed_asset_type_id: k._type === 'fixed_asset' ? (k.matched_fixed_asset_type_id || null) : null,
        quantity: parseFloat(k.quantity) || 0,
        unit: k.unit,
        package_qty: k._type !== 'product' || k.package_qty === '' ? null : parseFloat(k.package_qty),
        package_unit: k._type === 'product' ? (kart(k)?.unit || null) : null,
        unit_price: k.unit_price === '' ? null : parseFloat(k.unit_price),
        // Depo YALNIZCA stoklu kalemde — gider/demirbaş stoğa girmez
        warehouse_id: k._type === 'product' ? (k.warehouse_id || form.warehouse_id || null) : null,
        siparis_ref: k.siparis_ref || null,
      })),
    };
    try {
      let kayit;
      if (duzenleme) { kayit = (await api.patch(`/teslimat/${id}`, govde)).data; }
      else { kayit = (await api.post('/teslimat', govde)).data; }
      // Dosya kutusundan geldiyse belgeyi "işlendi"ye taşı — kutunun boşalması
      // görevin bitmesi demek (faturadaki akışın aynısı). Başarısız olursa
      // kaydı geri almıyoruz; belge kutuda kalır, tekrar işlenebilir.
      if (kutuBelgeId && !duzenleme) {
        try {
          await api.post(`/dosya-kutusu/${kutuBelgeId}/islendi`, {
            kayit_id: kayit.id, kayit_turu: 'mal_kabul',
            kayit_no: kayit.belge_no || '' });
        } catch { toast.info('Kayıt tamam ama belge kutuda kaldı'); }
      }
      toast.success(duzenleme ? 'Mal kabul güncellendi' : 'Mal kabul kaydedildi — stoğa işlendi');
      navigate('/erp/mal-kabul');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi', { duration: 8000 });
    }
    setKaydediliyor(false);
  };

  if (yukleniyor) {
    return <div className="p-6 text-[#6b5f4f]">Yükleniyor…</div>;
  }

  const GIRDI = 'h-9 text-sm border-[#d0c4b0]';

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={geri}
            className="text-[#6b5f4f]">
            <ArrowLeft size={15} className="mr-1" /> Geri
          </Button>
          <h1 className="text-lg font-semibold text-[#1a1916] flex items-center gap-2">
            <PackageCheck size={18} className="text-[#4a5a3a]" />
            {duzenleme ? 'Mal Kabul Düzenle' : 'Yeni Mal Kabul'}
          </h1>
        </div>
        <Button onClick={kaydet} disabled={kaydediliyor}
          className="bg-[#4a5a3a] hover:bg-[#3d4a30] text-white" size="sm"
          data-testid="mal-kabul-kaydet">
          <Save size={14} className="mr-1.5" />
          {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </div>

      {/* Görsel YALNIZCA geniş ekranda (xl) yanda: formun başlık ızgarası 6 sütunlu,
          daha dar ekranda yan panel formu sıkıştırıyordu — orada görsel üstte. */}
      <div className={gorsel ? 'grid xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4 items-start' : ''}>
      {gorsel && (
        <BelgeGorseli sayfalar={gorsel.sayfalar} sayfaAdresi={gorsel.adres}
          baslik="Mal kabul belgesi" className="xl:sticky xl:top-2 max-w-md xl:max-w-none mx-auto xl:mx-0 w-full"
          gorselClass="max-h-[38vh] xl:max-h-[78vh]" />
      )}
      <div className="min-w-0">
      {/* AI okuma — faturadaki akışın aynısı: belgeyi okut, formu doldur,
          kontrol et, kaydet. Okuma kayıt yapmaz. */}
      {!duzenleme && (
        <Card className="border-[#d0c4b0] mb-4 bg-[#faf7f2]">
          <CardContent className="py-3">
            <div className="flex items-center gap-3 flex-wrap">
              {kutuBelgeId ? (
                <span className="text-sm text-[#6b5f4f] flex items-center gap-1.5">
                  <Inbox size={15} className="text-[#4a5a3a]" />
                  Dosya kutusundan geldi — {okunuyor ? 'okunuyor…' : 'okundu'}
                </span>
              ) : (
                <>
                  <span className="text-sm text-[#6b5f4f] flex items-center gap-1.5">
                    <Sparkles size={15} className="text-[#4a5a3a]" />
                    İrsaliye / fişi okutup formu otomatik doldurun
                  </span>
                  <label className="inline-flex">
                    <input type="file" className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => { dosyadanOku(e.target.files?.[0]); e.target.value = ''; }}
                      data-testid="mal-kabul-dosya" />
                    <span className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded-md
                      border cursor-pointer transition-colors
                      ${okunuyor ? 'opacity-60 pointer-events-none' : ''}
                      border-[#4a5a3a] text-[#4a5a3a] hover:bg-[#4a5a3a]/10`}>
                      <Upload size={13} />
                      {okunuyor ? 'Okunuyor…' : 'Belge Yükle ve Oku'}
                    </span>
                  </label>
                </>
              )}
              {okunanCari && !form.supplier_id && (
                <span className="text-xs text-[#7a3535]">
                  {/* "Belgede X yazıyor" DEME: bu alan AI'nın okuma denemesi,
                      belgede yazanın kendisi değil — yanlış okuduğunda kullanıcıya
                      yanlış bilgi vermiş oluyoruz (faturada aynı hata yapılmıştı). */}
                  Okunan cari: "{okunanCari}" — listeden doğru cariyi seçin
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#d0c4b0] mb-4">
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-[#6b5f4f]">Cari Hesap *</Label>
              <SearchableSelect
                value={form.supplier_id}
                onChange={(v) => setForm(f => ({ ...f, supplier_id: v }))}
                options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                placeholder="Tedarikçi seçin"
                testId="mal-kabul-cari" />
            </div>
            <div>
              <Label className="text-xs text-[#6b5f4f]">Belge Türü</Label>
              <select value={form.belge_turu}
                onChange={(e) => setForm(f => ({ ...f, belge_turu: e.target.value }))}
                className={`${GIRDI} w-full rounded-md border bg-white px-2`}
                data-testid="mal-kabul-tur">
                {BELGE_TURLERI.map(t => (
                  <option key={t.deger} value={t.deger}>{t.etiket}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-[#6b5f4f]">Belge No</Label>
              <Input value={form.belge_no}
                onChange={(e) => setForm(f => ({ ...f, belge_no: e.target.value }))}
                className={GIRDI} placeholder="İrsaliye / fiş no" />
            </div>
            <MerkezSecici
              value={form.cost_center_id}
              onChange={(v) => setForm(f => ({ ...f, cost_center_id: v }))}
              etiketClass="text-xs text-[#6b5f4f] block mb-1.5"
              tetikClass={`${GIRDI} w-full bg-white border-[#d0c4b0]`}
            />
            <div>
              <Label className="text-xs text-[#6b5f4f]">Teslim Tarihi *</Label>
              <Input type="date" value={form.teslim_tarihi}
                onChange={(e) => setForm(f => ({ ...f, teslim_tarihi: e.target.value }))}
                className={GIRDI} data-testid="mal-kabul-tarih" />
              <span className="text-[10px] text-[#8a7e70]">stok bu güne işlenir</span>
            </div>
            <div>
              <Label className="text-xs text-[#6b5f4f]">Depo <span className="font-normal">(stoklu kalemler)</span></Label>
              <select value={form.warehouse_id}
                onChange={(e) => setForm(f => ({ ...f, warehouse_id: e.target.value }))}
                className={`${GIRDI} w-full rounded-md border bg-white px-2`}>
                <option value="">Kalem bazında</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Siparişten mal kabul — cariye ait açık sipariş kalemleri */}
      {siparisKalemleri.length > 0 && (
        <Card className="border-[#d0c4b0] mb-4">
          <CardContent className="pt-3 pb-3">
            <button onClick={() => setSiparisAcik(a => !a)}
              className="flex items-center gap-2 text-sm text-[#4a5a3a] font-medium"
              data-testid="siparis-ac">
              <Link2 size={14} />
              Bu cariden bekleyen {siparisKalemleri.length} sipariş kalemi var
              <span className="text-xs text-[#8a7e70]">
                {siparisAcik ? '(gizle)' : '(göster)'}
              </span>
            </button>
            {siparisAcik && (
              <div className="mt-3 border border-[#e8dfd0] rounded overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f5f1e8]">
                    <tr className="text-[11px] uppercase text-[#8a7e70]">
                      <th className="text-left p-2">Sipariş</th>
                      <th className="text-left p-2">Ürün</th>
                      <th className="text-right p-2">Sipariş</th>
                      <th className="text-right p-2">Gelen</th>
                      <th className="text-right p-2">Kalan</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {siparisKalemleri.map((sk, i) => (
                      <tr key={`${sk.order_id}-${sk.kalem_idx}`}
                        className="border-t border-[#e8dfd0]">
                        <td className="p-2 text-[#6b5f4f]">{sk.order_no}</td>
                        <td className="p-2 text-[#1a1916]">{sk.product_name}</td>
                        <td className="p-2 text-right font-mono">{sk.siparis_miktari}</td>
                        <td className="p-2 text-right font-mono text-[#8a7e70]">
                          {sk.teslim_alinan || 0}
                        </td>
                        <td className="p-2 text-right font-mono font-semibold">
                          {sk.kalan_miktar} {sk.unit}
                        </td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost"
                            onClick={() => siparistenEkle(sk)}
                            className="text-xs h-7 text-[#3d6e4e]"
                            data-testid={`siparis-ekle-${i}`}>
                            Ekle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-[#d0c4b0]">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#1a1916]">
              Kalemler <span className="text-[#8a7e70] font-normal">({kalemler.length})</span>
            </h2>
            <Button size="sm" variant="outline"
              onClick={() => setKalemler(p => [...p, bosKalem(form.warehouse_id)])}
              className="text-xs h-8 border-[#d0c4b0]" data-testid="kalem-ekle">
              <Plus size={13} className="mr-1" /> Kalem Ekle
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-[10px] uppercase text-[#8a7e70] border-b border-[#d0c4b0]">
                  <th className="text-left p-1.5 w-[30%]">Kalem</th>
                  <th className="text-right p-1.5">Amb. Adedi</th>
                  <th className="text-left p-1.5">Amb. Birimi</th>
                  <th className="text-right p-1.5">Amb. İçeriği</th>
                  <th className="text-right p-1.5">Birim Fiyat</th>
                  <th className="text-left p-1.5">Depo</th>
                  <th className="text-right p-1.5">Tutar</th>
                  <th className="p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {kalemler.map((k, i) => {
                  const p = kart(k);
                  const birimsiz = k.matched_product_id && !(k.unit || '').trim();
                  const stogaGiren = (parseFloat(k.quantity) || 0)
                    * (parseFloat(k.package_qty) > 0 ? parseFloat(k.package_qty) : 1);
                  return (
                    <tr key={k._yerel} className="border-b border-[#f0eae0] align-top">
                      <td className="p-1.5 space-y-1">
                        <div className="flex gap-1">
                          <select value={k._type || 'product'}
                            onChange={(e) => kalemGuncelle(i, '_type', e.target.value)}
                            className={`${GIRDI} w-24 shrink-0 rounded-md border bg-white px-1 text-xs`}
                            title="Kalem türü" data-testid={`kalem-tur-${i}`}>
                            {KALEM_TURLERI.map(t => <option key={t.deger} value={t.deger}>{t.etiket}</option>)}
                          </select>
                          <Input value={k.description || ''}
                            onChange={(e) => kalemGuncelle(i, 'description', e.target.value)}
                            className={`${GIRDI} flex-1 min-w-0`} placeholder="Belgedeki açıklama"
                            data-testid={`kalem-aciklama-${i}`} />
                        </div>
                        {(k._type || 'product') === 'product' ? (
                          <ProductCombobox
                            value={k.matched_product_id}
                            products={products}
                            onChange={(v) => kalemGuncelle(i, 'matched_product_id', v)}
                            testId={`kalem-urun-${i}`} />
                        ) : k._type === 'expense' ? (
                          <SearchableSelect value={k.matched_expense_id}
                            onChange={(v) => kalemGuncelle(i, 'matched_expense_id', v)}
                            options={giderKalemleri.map(g => ({ value: g.id, label: g.name, hint: g.code }))}
                            placeholder="Gider kalemi seçin" testId={`kalem-gider-${i}`} />
                        ) : (
                          <SearchableSelect value={k.matched_fixed_asset_type_id}
                            onChange={(v) => kalemGuncelle(i, 'matched_fixed_asset_type_id', v)}
                            options={demirbasTurleri.map(d => ({ value: d.id, label: d.name, hint: d.code }))}
                            placeholder="Demirbaş türü seçin" testId={`kalem-demirbas-${i}`} />
                        )}
                        {k.siparis_ref && (
                          <span className="text-[10px] text-[#3d6e4e] mt-0.5 block">
                            ↳ siparişten
                          </span>
                        )}
                      </td>
                      <td className="p-1.5">
                        <Input type="number" step="any" min="0" value={k.quantity}
                          onChange={(e) => kalemGuncelle(i, 'quantity', e.target.value)}
                          className={`${GIRDI} text-right w-24`} placeholder="0"
                          data-testid={`kalem-adet-${i}`} />
                      </td>
                      <td className="p-1.5">
                        <select value={k.unit || ''}
                          onChange={(e) => kalemGuncelle(i, 'unit', e.target.value)}
                          className={`${GIRDI} w-28 rounded-md border bg-white px-1.5
                            ${birimsiz ? 'border-[#7a3535] border-2 bg-[#fff0f0]' : ''}`}
                          title={birimsiz ? 'Belgedeki birimi seçin — boş bırakılamaz' : ''}
                          data-testid={`kalem-birim-${i}`}>
                          <option value="">—</option>
                          {p && birimSecenekleri(p, units).length > 0 && (
                            <optgroup label="★ Üründe tanımlı">
                              {birimSecenekleri(p, units).map(o => (
                                <option key={`t-${o.deger}`} value={o.deger}>★ {o.deger}</option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Diğer birimler">
                            {units.map(u => (
                              <option key={u.id} value={u.abbreviation}>{u.abbreviation}</option>
                            ))}
                          </optgroup>
                        </select>
                      </td>
                      <td className="p-1.5">
                        {(k._type || 'product') !== 'product' ? (
                          <span className="text-[11px] text-[#c0b8ad]">—</span>
                        ) : (<>
                        <div className="flex items-center gap-1">
                          <Input type="number" step="any" min="0" value={k.package_qty}
                            onChange={(e) => kalemGuncelle(i, 'package_qty', e.target.value)}
                            className={`${GIRDI} text-right w-20`} placeholder="1"
                            data-testid={`kalem-icerik-${i}`} />
                          <span className="text-[10px] text-[#8a7e70] w-8">{p?.unit || ''}</span>
                        </div>
                        {stogaGiren > 0 && p && (
                          <span className="text-[10px] text-[#3d6e4e] block mt-0.5">
                            ↳ {stogaGiren.toLocaleString('tr-TR')} {p.unit}
                          </span>
                        )}
                        </>)}
                      </td>
                      <td className="p-1.5">
                        <Input type="number" step="any" min="0" value={k.unit_price}
                          onChange={(e) => kalemGuncelle(i, 'unit_price', e.target.value)}
                          className={`${GIRDI} text-right w-28`} placeholder="opsiyonel"
                          data-testid={`kalem-fiyat-${i}`} />
                      </td>
                      <td className="p-1.5">
                        {(k._type || 'product') === 'product' ? (
                          <select value={k.warehouse_id || ''}
                            onChange={(e) => kalemGuncelle(i, 'warehouse_id', e.target.value)}
                            className={`${GIRDI} w-32 rounded-md border bg-white px-1.5`}
                            data-testid={`kalem-depo-${i}`}>
                            <option value="">— seçin —</option>
                            {warehouses.map(w => (
                              <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-[11px] text-[#8a7e70]" title="Gider ve demirbaş stoğa girmez">stok dışı</span>
                        )}
                      </td>
                      <td className="p-1.5 text-right font-mono text-[#1a1916]">
                        {((parseFloat(k.quantity) || 0) * (parseFloat(k.unit_price) || 0))
                          .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-1.5 text-right">
                        <Button size="sm" variant="ghost"
                          onClick={() => setKalemler(prev => prev.filter((_, j) => j !== i))}
                          className="h-7 w-7 p-0 text-[#7a3535]">
                          <Trash2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#d0c4b0]">
            <div className="flex-1 max-w-md">
              <Label className="text-xs text-[#6b5f4f]">Notlar</Label>
              <Input value={form.notlar}
                onChange={(e) => setForm(f => ({ ...f, notlar: e.target.value }))}
                className={GIRDI} placeholder="Opsiyonel" />
            </div>
            <div className="text-right">
              <span className="text-xs text-[#8a7e70] block">Toplam</span>
              <span className="text-lg font-semibold text-[#1a1916] font-mono">
                {toplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
      </div>
    </div>
  );
}
